import React, { useRef, useEffect } from 'react';
import { IonPage } from '@ionic/react';
import { ensureElementClassList } from '../utils/ionicNavigationFix';

interface SafeIonPageProps {
  children: React.ReactNode;
  id?: string;
  className?: string;
}

/**
 * Componente wrapper para IonPage que implementa las soluciones oficiales
 * de Ionic para prevenir errores de navegación classList undefined.
 *
 * Basado en las recomendaciones del equipo de Ionic para resolver:
 * "Cannot read properties of undefined (reading 'classList')"
 */
const SafeIonPage: React.FC<SafeIonPageProps> = ({ children, id, className }) => {
  const pageRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Asegurar que el elemento de la página tenga classList
    if (pageRef.current) {
      ensureElementClassList(pageRef.current);
      
      // Aplicar fix específico para elementos de navegación
      const ionContent = pageRef.current.querySelector('ion-content');
      if (ionContent) {
        ensureElementClassList(ionContent);
      }
      
      // Observer para elementos dinámicos
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node: any) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              ensureElementClassList(node);
              
              // Aplicar fix a elementos ion específicos
              if (node.tagName && node.tagName.startsWith('ION-')) {
                ensureElementClassList(node);
              }
            }
          });
        });
      });
      
      observer.observe(pageRef.current, {
        childList: true,
        subtree: true
      });
      
      // Cleanup
      return () => {
        observer.disconnect();
      };
    }
  }, []);

  return (
    <IonPage ref={pageRef} id={id} className={className}>
      {children}
    </IonPage>
  );
};

export default SafeIonPage;
